import { currentStep, daysBetween, localNow } from "../../src/cancellation.ts";
import { cardWords } from "../../src/money.ts";
import type { Item, Person } from "../../src/web/types.ts";
import { paidMoney, personName, roundMoney, weekday } from "./format.ts";

export { paidMoney };

// A booking's money and its cancellation, in words, for its card (Details.tsx), the booking form
// (BookingForm.tsx) and Bookings' rows.

const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const fmtDay = (date: string) => DAY.format(new Date(`${date}T00:00:00Z`));

// Each payment as its card says it, its how after it: ["SGD 609.93 paid by Meiwin", "Mastercard 8682"], "by you"
// for mine, "by a former member" for someone whose account is gone (`people`: everyone, former members too).
export function paymentFacts(p: NonNullable<Item["paid"]>[number], people: Person[], you: string): string[] {
  const name = personName(p.by, people, you);
  const who = name === "You" ? "you" : (name ?? "someone");
  return [`${paidMoney(p.amount, p.currency)} paid by ${who}`, ...(p.method ? [p.method] : [])];
}

// Which card a charge later goes on: its own few words ("Mastercard 8682"), else whose ("your card", "Mei's card").
export function cardOf(c: NonNullable<Item["charges"]>[number], people: Person[], you: string): string {
  if (c.method) return cardWords(c.method);
  const name = personName(c.by, people, you);
  return name === "You" ? "your card" : name ? `${name}'s card` : "a card on file";
}

// A charge later as its card says it (features/bookings-and-costs.md, "Charged later"): "SGD 600 charged to Mastercard
// 8682 on Sun 1 Nov"; from its day (`today`, this device's) while it's still to confirm, the question, "Charged on Sun
// 1 Nov?", with how much and which card after it.
export function chargeFacts(
  c: NonNullable<Item["charges"]>[number],
  people: Person[],
  you: string,
  today: string,
): { text: string[]; due: boolean } {
  const card = cardOf(c, people, you);
  const money = paidMoney(c.amount, c.currency);
  return c.on <= today
    ? { text: [`Charged on ${weekday(c.on)}?`, money, card], due: true }
    : { text: [`${money} charged to ${card} on ${weekday(c.on)}`], due: false };
}

const money = (a: { amount: number; currency: string }) => roundMoney(a.amount, a.currency);

type Step = NonNullable<NonNullable<Item["booking"]>["cancellation"]>["steps"][number];
const stepBy = (step: Step) =>
  `${fmtDay(step.until.slice(0, 10))}${step.until.length > 10 ? `, ${step.until.slice(11, 16)}` : ""}`;
// What a cancellation by a step's day gets back: "Free cancellation until 4 Dec, 00:00", "80% back if cancelled by 5 Dec".
const stepWords = (step: Step) => {
  const by = stepBy(step);
  return step.refund === "full"
    ? `Free cancellation until ${by}`
    : "percent" in step.refund
      ? `${step.refund.percent}% back if cancelled by ${by}`
      : `${money(step.refund)} back if cancelled by ${by}`;
};
const localOf = (i: Item, now: Date) =>
  localNow(now, i.when && "tz" in i.when ? i.when.tz : Intl.DateTimeFormat().resolvedOptions().timeZone);

// Where a booked thing's cancellation policy stands now: what a cancellation today would get back and
// until when, in the sun's colour within a week of the deadline. Nothing without a policy.
export function policyNote(i: Item, now = new Date()): { text: string; soon: boolean } | null {
  const policy = i.booking?.cancellation;
  if (!policy) return null;
  const local = localOf(i, now);
  const step = currentStep(policy, local);
  if (!step) return { text: policy.steps.length ? "Non-refundable now" : "Non-refundable", soon: false };
  const what = stepWords(step);
  const left = daysBetween(local, step.until);
  if (left > 7) return { text: what, soon: false };
  return { text: `${what} (${left === 0 ? "last day" : left === 1 ? "1 day left" : `${left} days left`})`, soon: true };
}

// A policy's every step, for a booking's card when it has more than one (DESIGN.md, "A card reads in sections": its
// Booking), then when nothing comes back: each said as the policy's note says the one in force, `past` once its day
// has gone, `now` the one a cancellation today falls in.
export function policySteps(i: Item, now = new Date()): { text: string; past: boolean; now: boolean }[] {
  const policy = i.booking?.cancellation;
  if (!policy || policy.steps.length < 2) return [];
  const step = currentStep(policy, localOf(i, now));
  const at = step ? policy.steps.indexOf(step) : policy.steps.length;
  const last = policy.steps[policy.steps.length - 1] as Step;
  return [
    ...policy.steps.map((s, n) => ({ text: stepWords(s), past: n < at, now: n === at })),
    { text: `Nothing back after ${stepBy(last)}`, past: false, now: at === policy.steps.length },
  ];
}
